import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLinkActive } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import type { IBiLink } from '../../../../shared/interfaces/bi-link.interface';

@Component({
  standalone: false,
  selector: 'suvo-bi-list-navigation-item',
  templateUrl: './list-navigation-item.component.html',
  styleUrls: ['./list-navigation-item.component.scss'],
})
export class ListNavigationItemComponent implements OnInit, OnDestroy {
  @ViewChild('routerLinkActiveRef') routerLinkActiveRef: RouterLinkActive;

  @Input() routePrefix: string[] | string = [];
  @Input() depth = 0;
  @Input() borderRadius: string = '24px';
  @Input() theme: string;
  @Input() showIcons: boolean = false;
  @Input() link: IBiLink;

  @Output() onLinkClick = new EventEmitter();

  expanded = false;

  $unsubscribe = new Subject<void>();

  constructor(private router: Router) {}

  handleLinkClick(event?: Event) {
    if (this.link.disabled) {
      return;
    }

    if (!this.hasNavigableRoute()) {
      event?.preventDefault();

      const firstDescendantRoute = this.getFirstNavigableDescendantRoute(this.link);
      if (firstDescendantRoute) {
        this.navigateToRoute(firstDescendantRoute);
      }
    }

    if (this.onLinkClick) this.onLinkClick.emit(this.link);
  }

  handleExpandButtonClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.link?.disabled || !this.link?.subLinks?.length) {
      return;
    }

    this.link.dropDownOpen = !this.link.dropDownOpen;
  }

  ngOnInit() {
    this.router.events.pipe(takeUntil(this.$unsubscribe)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateDropDownState(event.urlAfterRedirects ?? event.url);
      }
    });

    this.updateDropDownState(this.router.url);
  }

  ngOnDestroy() {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  isLoosengeLink() {
    return this.depth !== 0 || (this.depth === 0 && !this.link.subLinks);
  }

  private updateDropDownState(currentUrl: string) {
    if (!this.link?.subLinks?.length) {
      return;
    }

    this.link.dropDownOpen =
      this.linkIsActive(currentUrl, this.link) ||
      this.link.subLinks.some((subLink) => this.linkIsActive(currentUrl, subLink));
  }

  private linkIsActive(currentUrl: string, link: IBiLink) {
    const normalizedCurrentUrl = this.normalizePath(currentUrl);
    const candidates = this.resolveRouteCandidates(link.route);

    return candidates.some((candidate) => {
      const normalizedCandidate = this.normalizePath(candidate);
      if (normalizedCandidate === '/') {
        return normalizedCurrentUrl === normalizedCandidate;
      }

      return (
        normalizedCurrentUrl === normalizedCandidate ||
        normalizedCurrentUrl.startsWith(`${normalizedCandidate}/`)
      );
    });
  }

  private resolveRouteCandidates(route: IBiLink['route']): string[] {
    if (!route) {
      return [];
    }

    const routeAsPath = Array.isArray(route)
      ? `/${route
          .filter((segment) => !!segment)
          .map((segment) => `${segment}`.replace(/^\/+|\/+$/g, ''))
          .filter((segment) => !!segment)
          .join('/')}`
      : `${route}`;

    const normalizedRoutePath = this.normalizePath(routeAsPath);
    const prefix = this.normalizePath(this.routePrefixToPath());
    const candidates = new Set<string>([normalizedRoutePath]);

    if (!normalizedRoutePath.startsWith('/private/')) {
      candidates.add(this.normalizePath(`/private/${normalizedRoutePath}`));
    }

    if (prefix && prefix !== '/' && !normalizedRoutePath.startsWith(`${prefix}/`)) {
      candidates.add(this.normalizePath(`${prefix}/${normalizedRoutePath}`));
    }

    return Array.from(candidates);
  }

  hasNavigableRoute() {
    return this.resolveRouteCandidates(this.link?.route).length > 0;
  }

  private getFirstNavigableDescendantRoute(link: IBiLink): IBiLink['route'] | undefined {
    if (!link?.subLinks?.length) {
      return undefined;
    }

    for (const subLink of link.subLinks) {
      if (this.resolveRouteCandidates(subLink.route).length > 0) {
        return subLink.route;
      }

      const nestedRoute = this.getFirstNavigableDescendantRoute(subLink);
      if (nestedRoute) {
        return nestedRoute;
      }
    }

    return undefined;
  }

  private navigateToRoute(route: IBiLink['route']) {
    if (!route) {
      return;
    }

    if (Array.isArray(route)) {
      this.router.navigate(route);
      return;
    }

    this.router.navigateByUrl(route);
  }

  private routePrefixToPath() {
    if (Array.isArray(this.routePrefix)) {
      return this.routePrefix
        .filter((segment) => !!segment)
        .map((segment) => `${segment}`.replace(/^\/+|\/+$/g, ''))
        .filter((segment) => !!segment)
        .join('/');
    }

    return this.routePrefix;
  }

  private normalizePath(path: string) {
    if (!path) {
      return '/';
    }

    const withoutQueryOrFragment = path.split('?')[0].split('#')[0];
    const withLeadingSlash = withoutQueryOrFragment.startsWith('/')
      ? withoutQueryOrFragment
      : `/${withoutQueryOrFragment}`;

    const normalized = withLeadingSlash.replace(/\/{2,}/g, '/').replace(/\/$/, '');
    return normalized || '/';
  }
}
